import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, qs } from '../lib/api';
import type { Page } from '../lib/api';
import { date, mobile as fmtMobile, num } from '../lib/format';
import { useListParams } from '../components/DataTable';
import {
  Badge,
  Button,
  Card,
  CardHead,
  Empty,
  Notice,
  SearchInput,
  StatusBadge,
  TableSkeleton,
} from '../components/ui';

/** The API's hard page-size cap (an unbounded limit is a DoS vector). */
const TEAM_LIMIT = 100;

interface AgentRow {
  id: string;
  depth: number;
  referralCode: string;
  treePath: string;
  isActive: boolean;
  enrolledAt: string;
  user: { id: string; mobile: string; displayName: string | null; kycStatus: string; isBlocked: boolean };
  sponsor: { id: string; referralCode: string; user: { displayName: string | null } } | null;
  _count: { downline: number; crmCustomers: number };
}

/**
 * v2 §12–15 — one partner's team.
 *
 * The Partners page answers "who are all the partners"; this answers "who is
 * under this one", which is the question an operator actually has when a
 * partner calls about their team or their bonuses.
 *
 * Grouped by level rather than drawn as a tree. A tree is the truthful shape,
 * but reading one in a support call means tracing a line with a finger; the
 * levels are what people say out loud — "the two she enrolled, and the three
 * under them".
 */
export function TeamPage() {
  const { get, set } = useListParams();
  const search = get('search');
  const rootId = get('partner');

  // The whole subtree in one request. The API resolves it from the
  // materialised treePath, so depth costs nothing — walking level by level
  // from here would be a request per level and a spinner per level with it.
  //
  // 100 is the API's hard cap on page size, not a guess. A team larger than
  // that is truncated, and says so below rather than quietly showing a
  // partial tree as if it were the whole one.
  const team = useQuery({
    queryKey: ['team', rootId],
    queryFn: () => api.get<Page<AgentRow>>(`/admin/agents${qs({ teamOf: rootId, limit: TEAM_LIMIT })}`),
    enabled: Boolean(rootId),
  });

  const picker = useQuery({
    queryKey: ['team-picker', search],
    queryFn: () => api.get<Page<AgentRow>>(`/admin/agents${qs({ search, limit: 25 })}`),
  });

  const members = team.data?.items ?? [];
  const total = team.data?.meta.total ?? 0;
  const truncated = total > members.length;
  const selected = picker.data?.items.find((a) => a.id === rootId);

  // Depth is absolute in the tree, so a partner three levels down has a team
  // whose depths start at four. Levels are shown relative to the partner
  // being looked at, which is how anyone describes their own team.
  const baseDepth = selected?.depth ?? (members.length ? Math.min(...members.map((m) => m.depth)) - 1 : 0);
  const byLevel = new Map<number, AgentRow[]>();
  for (const m of members) {
    const level = m.depth - baseDepth;
    byLevel.set(level, [...(byLevel.get(level) ?? []), m]);
  }
  const levels = [...byLevel.keys()].sort((a, b) => a - b);

  const inactive = members.filter((m) => !m.isActive).length;
  const leads = members.reduce((t, m) => t + m._count.crmCustomers, 0);

  return (
    <div className="stack">
      <Card>
        <CardHead
          title="Choose a partner"
          sub="Their whole team, at every level beneath them (FR-AGT-07)."
          action={<SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Name, mobile or code…" />}
        />
        {picker.isLoading ? (
          <table className="table">
            <TableSkeleton rows={3} cols={3} />
          </table>
        ) : picker.data?.items.length ? (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', padding: '0 16px 16px' }}>
            {picker.data.items.map((a) => (
              <button
                key={a.id}
                className={`chip ${a.id === rootId ? 'chip--active' : ''}`}
                onClick={() => set('partner', a.id === rootId ? '' : a.id)}
              >
                {a.user.displayName ?? fmtMobile(a.user.mobile)}
                <span className="chip__count">{a._count.downline}</span>
              </button>
            ))}
          </div>
        ) : (
          <Empty
            icon="⚯"
            title="No partners match"
            body="Partners are enrolled by other partners — there is no self-registration (FR-AGT-01)."
          />
        )}
      </Card>

      {!rootId ? (
        <Empty icon="⚯" title="Pick a partner above" body="Their team appears here, grouped by level." />
      ) : team.isLoading ? (
        <Card>
          <table className="table">
            <TableSkeleton rows={5} cols={4} />
          </table>
        </Card>
      ) : team.isError ? (
        // Never the empty state on a failure. "Has enrolled nobody" is a claim
        // about this partner; if the request failed we do not know that, and
        // saying it would send someone to argue with a partner about a team
        // they can see in their own app.
        <Notice tone="danger" title="Could not load this team">
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <span>{(team.error as Error).message}</span>
            <Button size="sm" variant="ghost" onClick={() => void team.refetch()}>
              Try again
            </Button>
          </div>
        </Notice>
      ) : members.length === 0 ? (
        <Empty
          icon="⚯"
          title="This partner has enrolled nobody yet"
          body="Their team fills in as they enrol others, and every level below counts towards it."
        />
      ) : (
        <>
          <Card>
            <CardHead
              title={selected ? (selected.user.displayName ?? fmtMobile(selected.user.mobile)) : 'Team'}
              sub={selected ? `${selected.referralCode} · enrolled ${date(selected.enrolledAt)}` : undefined}
              action={
                selected && (
                  <Link to={`/users/${selected.user.id}`}>
                    <Button size="sm">Open account</Button>
                  </Link>
                )
              }
            />
            <div className="row" style={{ gap: 20, padding: '0 16px 16px', flexWrap: 'wrap' }}>
              <Stat label="Team size" value={num(members.length)} />
              <Stat label="Levels deep" value={num(levels.length)} />
              <Stat label="Direct" value={num(byLevel.get(1)?.length ?? 0)} />
              <Stat label="CRM leads held" value={num(leads)} />
              <Stat label="Suspended" value={num(inactive)} tone={inactive ? 'amber' : undefined} />
            </div>
          </Card>

          {truncated && (
            <Notice tone="warn" title={`Showing ${members.length} of ${num(total)}`}>
              This team is larger than one page. The levels below are complete only as far as they go — open a
              specific member to see their own team rather than reading this as the whole tree.
            </Notice>
          )}

          {inactive > 0 && (
            <Notice tone="warn" title={`${inactive} of this team is suspended`}>
              A suspended partner keeps their place in the tree — removing them would reparent everyone below and
              rewrite a bonus history that has to stay auditable. They simply cannot enrol or earn while suspended.
            </Notice>
          )}

          {levels.map((level) => (
            <Card key={level}>
              <CardHead
                title={level === 1 ? 'Directly enrolled' : `Level ${level}`}
                sub={`${byLevel.get(level)!.length} ${byLevel.get(level)!.length === 1 ? 'partner' : 'partners'}`}
              />
              <table className="table">
                <thead>
                  <tr>
                    <th>Partner</th>
                    <th>Referral code</th>
                    <th>Sponsor</th>
                    <th style={{ textAlign: 'right', paddingRight: 18 }}>Direct</th>
                    <th style={{ textAlign: 'right', paddingRight: 18 }}>CRM leads</th>
                    <th>KYC</th>
                    <th>Enrolled</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {byLevel.get(level)!.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <div className="cell-strong">
                          {m.user.displayName ?? 'Unnamed'}
                          {!m.isActive && (
                            <span style={{ marginLeft: 6 }}>
                              <Badge tone="amber">Suspended</Badge>
                            </span>
                          )}
                          {m.user.isBlocked && (
                            <span style={{ marginLeft: 6 }}>
                              <Badge tone="red">Blocked</Badge>
                            </span>
                          )}
                        </div>
                        <div className="cell-muted">{fmtMobile(m.user.mobile)}</div>
                      </td>
                      <td>
                        <span className="cell-mono">{m.referralCode}</span>
                      </td>
                      <td>{m.sponsor?.user.displayName ?? <span className="cell-muted">—</span>}</td>
                      <td style={{ textAlign: 'right', paddingRight: 18 }}>{num(m._count.downline)}</td>
                      <td style={{ textAlign: 'right', paddingRight: 18 }}>{num(m._count.crmCustomers)}</td>
                      <td>
                        <StatusBadge value={m.user.kycStatus} />
                      </td>
                      <td>
                        <span className="cell-muted">{date(m.enrolledAt)}</span>
                      </td>
                      <td>
                        <div className="cell-actions">
                          {m._count.downline > 0 && (
                            <Button size="sm" variant="ghost" onClick={() => set('partner', m.id)}>
                              Their team
                            </Button>
                          )}
                          {m._count.crmCustomers > 0 && (
                            <Link to={`/crm?agent=${m.id}`}>
                              <Button size="sm" variant="ghost">
                                Leads
                              </Button>
                            </Link>
                          )}
                          <Link to={`/users/${m.user.id}`}>
                            <Button size="sm">Account</Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'amber' }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: tone === 'amber' ? 'var(--amber)' : 'var(--navy)' }}>
        {value}
      </div>
      <div className="cell-muted">{label}</div>
    </div>
  );
}
